import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomInput, CustomToggle } from '../Components/CustomFormElements';
import SureLoader from '../Components/SureLoader';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';

const NewOrgPageContent = ({ canEdit }) => {
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { createOrganization } = useOrganizationApi();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [isActive, setIsActive] = useState(true);
  // ✅ Validation Function
  const validateOrganization = () => {
    const field = document.getElementById('organization');

    if (!orgName.trim()) {
      field?.classList.add('input-error');
      showAlert('fail', 'Organization name is required.');
      return false;
    }

    if (/[^a-zA-Z0-9\s]/.test(orgName)) {
      field?.classList.add('input-error');
      showAlert('fail', 'Organization name should not contain special characters.');
      return false;
    }

    field?.classList.remove('input-error');
    return true;
  };

  // ✅ Handle Create Organization
  const handleCreateOrganization = async () => {
    if (!validateOrganization()) {
      return;
    }

    try {
      setLoading(true);
      await createOrganization({
        title: orgName,
        description: '',
      }); // Call API to create organization

      // ✅ Navigate to home and pass success message via state
      navigate('/', {
        state: {
          successMessage: `Organization "${orgName}" created successfully!`,
        },
      });

      console.log('Organization Created:', { orgName, isActive });
    } catch (error) {
      showAlert('fail', 'Failed to create organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Cancel with Confirmation
  const handleCancel = () => {
    showConfirmation('Are you sure you want to cancel creating a new organization?', () => {
      navigate('/'); // Navigate back to home or organization list
    });
  };

  // ✅ Reset Error Class on Input Focus
  useEffect(() => {
    const inputField = document.getElementById('organization');
    if (inputField) {
      inputField.addEventListener('focus', () => {
        inputField.classList.remove('input-error');
      });
    }
  }, []);

  return (
    <div className="container">
      {loading && <SureLoader fullscreen />}
      <div className="row gap">
        <div className="d-flex radius-yes p-2 bg-yes col-12 flex-column gap">
          <div className="int-col-4 p-2">
            <h1 className="h1-title">Create New Organization</h1>
          </div>

          <div className="int-col-4 gap d-flex flex-row-to-col">
            <div className="int-col-2 p-2 border-yes radius-yes full-width-on-sml-scr">
              {/* Organization Name Input */}
              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Organization Name</div>
                  <div className="small-font">Enter your organization's name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="organization"
                    label="Organization Name"
                    placeholder="Enter organization name"
                    value={orgName}
                    onChange={(id, value) => setOrgName(value)}
                    type="text"
                  />
                </div>
              </div>

              {/* Toggle for Active/Inactive */}
              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Status</div>
                  <div className="small-font">Set organization as Active or Inactive</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive} // Use checked to control toggle state
                    onChange={(id, value) => setIsActive(value)}
                    disabled={false}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="buttons-block w-100 justify-end d-flex flex-row flex-row-to-col gap">
                {canEdit && (
                  <Button background="color2" size="large" onClick={handleCreateOrganization}>
                    Create Organization
                  </Button>
                )}
                <Button background="color0" size="large" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ Wrap with ConfirmationProvider
const NewOrg = ({ canEdit }) => (
  <ConfirmationProvider>
    <NewOrgPageContent canEdit={canEdit} />
  </ConfirmationProvider>
);

export default NewOrg;
